define('extensions/dc/dc.extensions',[
    'dc.base',
    'lib/d3',
    'lib/lodash4',
    'moment'
], function (
    dc,
    d3,
    _,
    moment
) {
    'use strict';

    // =======================================================================
    // Helper Functions
    // =======================================================================

    // Given a node for a lineChart <circle> element, duplicate that node,
    // and return the duplicated node.

    function cloneNodeForTooltip(source,seriesIndex,chart) {

        var clone = source.cloneNode(true);
        var cloneD3 = d3.select(clone);
        var sourceD3 = d3.select(source);
        var sourceParentD3 = d3.select(source.parentNode);

        cloneD3.data(sourceD3.data());
        cloneD3.attr('class','dot sub _'+seriesIndex)
                .attr('cx', function (d) {
                    return dc.utils.safeNumber(chart.x()(d.x));
                })
                .attr('cy', function (d) {
                    if (chart.children()[seriesIndex].useRightYAxis()) {
                        return dc.utils.safeNumber(chart.rightY()(d.y + d.y0));
                    } else {
                        return dc.utils.safeNumber(chart.y()(d.y + d.y0));
                    }
                })
                .attr('data-inintest', function(d) {
                    return d.layer + '_' + moment(d.x).toISOString() + '_' + d.y;
                });

        if(sourceParentD3.style("display") !== "none"){
            return clone;
        }

        return false;

    }

    // Given a chart and it's parent, build a CSS selector to that can be
    // used to identifiy chart elements within a composite chart.

    function buildLayerSelector(parent, chart) {

        var layerIndex = _.findIndex(parent.children(), function(childChart) {
            return chart.chartID() === childChart.chartID();
        });

        return layerIndex;

    }

    // =======================================================================
    // Extensions to Composite Chart
    // =======================================================================

    dc.extendedCompositeChart = function(parent, chartGroup) {

        var _chart = dc.compositeChart(parent, chartGroup);

        _chart.dots = [];

        // Reorders dot nodes so that they aren't drawn such that
        // they obscure one another for achieving a focus state
        // on mouseover events.

        _chart.reorderDots = function() {

            // remove all dots in aggregate container
            _chart.selectAll('g.aggregate-tooltip-container').remove();

            var gContainer = _chart.select('g');
            var ttContainers = _chart.selectAll('g.dc-tooltip');

            if(_chart.dots.length > 0){
                _chart.dots.length = 0;
            }

            _.each(ttContainers[0], function (container) {
                // Make sure the dots are in ascending order by their X-axis value
                var gDots = d3.select(container).selectAll('circle.dot').sort(function(a, b) {
                    return a.x < b.x ? -1 : a.x > b.x ? 1 : a.x >= b.x ? 0 : NaN;
                });

                _chart.dots.push(gDots[0]);
            });

            var tooltipContainer = gContainer.append('g')
                .attr('class','aggregate-tooltip-container')
                .attr('transform', 'translate(' +
                    _chart.margins().left + ', ' +
                    _chart.margins().top + ')');

            // append duplicate nodes to new aggregate layer

            _.each(_chart.dots, function(dotArr, index) {

                var seriesIndex = index;

                _.each(dotArr, function (dot) {

                    var newNode = cloneNodeForTooltip(dot, seriesIndex, _chart);

                    if(newNode){
                        tooltipContainer.append(function () {
                            return newNode;
                        });
                    }

                });

            });

        };

        // The edge version of DC.js still doesn't full handle brush filtering
        // on composite charts so we're using a custom brush filter.

        _chart._brushing = function () {

            var extent = _chart.extendBrush();
            var rangedFilter = null;
            if(!_chart.brushIsEmpty(extent)) {
                rangedFilter = dc.filters.RangedFilter(extent[0], extent[1]);
            }

            dc.events.trigger(function () {
                if (!rangedFilter) {
                    _chart.filter(null);
                } else {
                    _chart.replaceFilter(rangedFilter);
                }
                _chart.redrawGroup();
            }, dc.constants.EVENT_DELAY);

        };

        // The rescale() function isn't called on children.
        // In the case of the barCharts in composite charts,
        // this is problematic, as it only recalculates the
        // barWidth when rescale() is called.

        _chart.resize = function (width, height){

            var children = _chart.children();

            _chart.width(width);
            _chart.height(height);

            _chart.rescale();

            _(children).each(function (child) {
                child.width(_chart.width());
                child.height(_chart.height());
                child.rescale();
            });

            _chart.render();

        };

        return _chart;

    };

    // =======================================================================
    // Extensions to Line Chart
    // =======================================================================

    dc.extendedLineChart = function(parent, chartGroup) {

        var _chart = dc.lineChart(parent, chartGroup);

        var _chartIsActive = true;

        // Tracks active/inactive state of the current chart.

        _chart.chartIsActive = function() {

            return _chartIsActive;

        };

        // Toggles the state of the current chart between
        // active/inactive states.

        _chart.toggleVisibility = function() {

            var chartContainer = _chart.svg().select("g");
            var className = ".sub._" + buildLayerSelector(parent, _chart);
            var tooltipClassName = "._" + buildLayerSelector(parent, _chart) + " .dc-tooltip";

            _chartIsActive = !_chartIsActive;

            if(_chartIsActive){
                chartContainer.selectAll(className).style("display","block");
                chartContainer.selectAll(tooltipClassName).style("display","block");
            } else {
                chartContainer.selectAll(className).style("display","none");
                chartContainer.selectAll(tooltipClassName).style("display","none");
            }

            parent.reorderDots();

        };

        // After a redraw or rerender, DC.js will set svg DOM
        // nodes back visible and we need to reset them to the
        // correct state of visibility.

        _chart.restoreVisibilityState = function() {

            var chartContainer = _chart.svg().select("g");
            var className = ".sub._" + buildLayerSelector(parent, _chart);
            var tooltipClassName = "._" + buildLayerSelector(parent, _chart) + " .dc-tooltip";

            if(_chartIsActive){
                chartContainer.selectAll(className).style("display","block");
                chartContainer.selectAll(tooltipClassName).style("display","block");
            } else {
                chartContainer.selectAll(className).style("display","none");
                chartContainer.selectAll(tooltipClassName).style("display","none");
            }

            parent.reorderDots();

        };

        return _chart;

    };

    // =======================================================================
    // Extensions to bar Chart
    // =======================================================================

    dc.extendedBarChart = function(parent, chartGroup) {

        var MIN_BAR_WIDTH = 1;
        var DEFAULT_GAP_BETWEEN_BARS = 2;

        var _gap = DEFAULT_GAP_BETWEEN_BARS;
        var _centerBar = false;
        var _chartIsActive = true;
        var _barWidth;

        var _chart = dc.barChart(parent, chartGroup);

        // Tracks active/inactive state of the current chart.

        _chart.chartIsActive = function() {

            return _chartIsActive;

        };

        // Toggles the state of the current chart between
        // active/inactive states.

        _chart.toggleVisibility = function() {

            var chartContainer = _chart.svg().select("g");
            var className = ".sub._" + buildLayerSelector(parent, _chart);

            _chartIsActive = !_chartIsActive;

            if(_chartIsActive){
                chartContainer.selectAll(className).style("display","block");
            } else {
                chartContainer.selectAll(className).style("display","none");
            }

        };

        // After a redraw or rerender, DC.js will set svg DOM
        // nodes back visible and we need to reset them to the
        // correct state of visibility.

        _chart.restoreVisibilityState = function() {

            var chartContainer = _chart.svg().select("g");
            var className = ".sub._" + buildLayerSelector(parent, _chart);

            if(_chartIsActive){
                chartContainer.selectAll(className).style("display","block");
            } else {
                chartContainer.selectAll(className).style("display","none");
            }

        };

        // Copied over & overriding dc.barChart.plotData so we can call our own
        // local version of calculateBarWidth().

        _chart.plotData = function () {

            var layers = _chart.chartBodyG().selectAll('g.stack')
                .data(_chart.data());

            calculateBarWidth();

            layers
                .enter()
                .append('g')
                .attr('class', function (d, i) {
                    return 'stack ' + '_' + i;
                });

            layers.each(function (d, i) {
                var layer = d3.select(this);
                renderBars(layer, i, d);
            });

        };

        // Copied over & overriding dc.barChart.plotData so we can call our own
        // local version of calculateBarWidth().

        function barHeight(d) {
            return dc.utils.safeNumber(Math.abs(_chart.y()(d.y + d.y0) - _chart.y()(d.y0)));
        }

        // Copied over & overriding dc.barChart.plotData so we can call our own
        // local version of calculateBarWidth().

        function onClick(d) {
            _chart.onClick(d.data);
        }

        // Copied over & overriding dc.barChart.plotData so we can call our own
        // local version of calculateBarWidth().

        function renderBars(layer, layerIndex, d) {

            var bars = layer.selectAll('rect.bar')
                .data(d.values, dc.pluck('x'));

            var enter = bars.enter()
                .append('rect')
                .attr('class', 'bar')
                .attr('fill', dc.pluck('data', _chart.getColor))
                .attr('height', 0);

            if (_chart.renderTitle()) {
                enter.append('title').text(dc.pluck('data', _chart.title(d.name)));
            }

            if (_chart.isOrdinal()) {
                bars.on('click', onClick);
            }

            dc.transition(bars, _chart.transitionDuration())
                .attr('x', function (d) {
                    var x = _chart.x()(d.x);
                    if (_centerBar) {
                        x -= _barWidth / 2;
                    }
                    if (_chart.isOrdinal() && _gap !== undefined) {
                        x += _gap / 2;
                    }
                    return dc.utils.safeNumber(x);
                })
                .attr('y', function (d) {
                    var y = _chart.y()(d.y + d.y0);

                    if (d.y < 0) {
                        y -= barHeight(d);
                    }

                    return dc.utils.safeNumber(y);
                })
                .attr('width', _barWidth)
                .attr('height', function (d) {
                    return barHeight(d);
                })
                .attr('fill', dc.pluck('data', _chart.getColor))
                .select('title').text(dc.pluck('data', _chart.title(d.name)));

            dc.transition(bars.exit(), _chart.transitionDuration())
                .attr('height', 0)
                .remove();

        }

        // Calculates width of bar chart columns so that we can more apporpriately
        // size columns in multi-series composite charts as well as resize them.

        function calculateBarWidth() {

            var numberOfBars = _chart.xUnitCount();
            var numberOfSeries = (parent) ? parent.children().length : 1;
            var numberOfBarsPerSeries = numberOfBars/numberOfSeries;

            if (_chart.isOrdinal() && _gap === undefined) {

                _barWidth = Math.floor(_chart.x().rangeBand());

            } else if (_gap) {

                // This calculation has been altered for the extend chart type.

                _barWidth = Math.floor(((_chart.xAxisLength()/(numberOfBarsPerSeries + 1))/numberOfSeries)-((numberOfSeries)*_gap));

            } else {

                // This calculation has been altered for the extend chart type.

                _barWidth = Math.floor((_chart.xAxisLength()/(numberOfBarsPerSeries + 1))/numberOfSeries);

            }

            if (_barWidth === Infinity || isNaN(_barWidth) || _barWidth < MIN_BAR_WIDTH) {
                _barWidth = MIN_BAR_WIDTH;
            }

        }

        return _chart;

    };

    return dc;

});

